<script setup>
import { computed } from 'vue';
import XlAvatar from '../xl/XlAvatar.vue';
import XlTag from '../xl/XlTag.vue';
import XlTooltip from '../xl/XlTooltip.vue';

const props = defineProps({
  brokers: { type: Array, default: () => [] },
  modelValue: { type: [Number, null], default: null },
  // The broker this person is with today, marked so "assign" can never be
  // confused with "leave them where they are".
  currentBrokerId: { type: [Number, null], default: null },
  // How many people this choice would move, so each row can show the book size
  // it is about to become.
  willChange: { type: Number, default: 0 },
  allowNobody: { type: Boolean, default: false },
});

const emit = defineEmits(['update:modelValue']);

const nameOf = broker => `${broker.first_name ?? ''} ${broker.last_name ?? ''}`.trim() || broker.email;

// Every broker on the smallest book carries the badge, so a tie shows as one.
// When every book is the same size there is nothing to tell apart.
const lightestIds = computed(() => {
  const sizes = props.brokers.map(b => b.book_size ?? 0);
  const min = Math.min(...sizes);

  if (sizes.every(size => size === min)) return new Set();

  return new Set(props.brokers.filter(b => (b.book_size ?? 0) === min).map(b => b.id));
});

const select = (id) => emit('update:modelValue', id);
</script>

<template>
  <div class="broker-picker" data-testid="broker-picker">
    <button
      v-if="allowNobody"
      type="button"
      class="broker-picker__row broker-picker__row--nobody"
      :class="{ 'broker-picker__row--on': modelValue === null }"
      data-testid="broker-picker-nobody"
      @click="select(null)"
    >
      <span class="broker-picker__avatar">—</span>
      <span class="broker-picker__body">
        <span class="broker-picker__name">
          Nobody yet
          <!-- Where it stands, not a choice somebody made. -->
          <span v-if="currentBrokerId === null" class="xl-tag">how it stands now</span>
        </span>
        <span class="broker-picker__meta">They will have nobody to message until someone is assigned.</span>
      </span>
    </button>

    <button
      v-for="broker in brokers"
      :key="broker.id"
      type="button"
      class="broker-picker__row"
      :class="{ 'broker-picker__row--on': modelValue === broker.id }"
      :data-testid="`broker-picker-${broker.id}`"
      @click="select(broker.id)"
    >
      <XlAvatar :name="nameOf(broker)" :src="broker.photo || ''" :size="38" />
      <span class="broker-picker__body">
        <span class="broker-picker__name">
          {{ nameOf(broker) }}
          <XlTag v-if="broker.id === currentBrokerId">their broker now</XlTag>
        </span>
        <span class="broker-picker__meta">
          {{ broker.book_size }} {{ broker.book_size === 1 ? 'policyholder' : 'policyholders' }}
          <template v-if="modelValue === broker.id && willChange">
            → {{ broker.book_size + willChange }} after this
          </template>
        </span>
      </span>
      <XlTooltip
        v-if="lightestIds.has(broker.id)"
        text="This broker looks after the fewest policyholders right now."
        control
        :data-testid="`broker-picker-${broker.id}-smallest`"
      >
        <XlTag tone="success">smallest book</XlTag>
      </XlTooltip>
    </button>

    <!--
      A broker is a member of the agency's staff, appointed with the rest of
      them — not a customer promoted from this list. Pointing anywhere on this
      page would send the reader somewhere that cannot do it.
    -->
    <p v-if="!brokers.length" class="broker-picker__empty" data-testid="broker-picker-empty">
      Nobody here has been made a broker yet. Brokers are appointed under
      <b>Team &amp; Users</b>, with the rest of the agency's staff — give somebody the broker role
      in this hub and they can be given policyholders from then on.
    </p>
  </div>
</template>

<style lang="scss" scoped>
// The prototype's `.sel` row.
.broker-picker {
  display: flex;
  flex-direction: column;
  gap: 8px;
  font-family: var(--xl-font-family);

  &__row {
    display: flex;
    align-items: center;
    gap: 10px;
    width: 100%;
    padding: 13px 15px;
    text-align: left;
    // A button resets what text it carries; the prototype's row is a div.
    font: inherit;
    letter-spacing: inherit;
    color: inherit;
    background: #fff;
    border: 1px solid var(--xl-color-border);
    border-radius: 12px;
    cursor: pointer;

    &:hover { border-color: var(--xl-color-primary-fixed-dim); }

    &:focus-visible {
      outline: 2px solid var(--xl-color-primary);
      outline-offset: 2px;
    }

    &--on {
      border-color: var(--xl-color-primary);
      background: var(--xl-color-primary-container);
    }

    // Nobody is a state, not a person: dashed, and never filled the way a
    // chosen broker is, so an unadvised policyholder does not open looking as
    // though somebody had already been picked for them.
    &--nobody {
      border-style: dashed;

      &.broker-picker__row--on { background: transparent; }

      .broker-picker__name {
        font-weight: var(--xl-font-weight-regular);
        color: var(--xl-color-secondary-text);
      }
    }
  }

  &__avatar {
    display: flex;
    align-items: center;
    justify-content: center;
    flex: 0 0 38px;
    width: 38px;
    height: 38px;
    border-radius: 50%;
    background: var(--xl-color-border);
    color: var(--xl-color-secondary-text);
    font-size: 13px;
    font-weight: 600;
  }

  &__body {
    display: flex;
    flex-direction: column;
    flex: 1;
    min-width: 0;
  }

  // 22px: the prototype's name sits in a line of the page's 16px text.
  &__name {
    display: flex;
    align-items: center;
    gap: 8px;
    line-height: 22px;
    font-size: 14.5px;
    font-weight: 700;
    color: var(--xl-color-primary-text);
  }

  &__meta {
    font-size: 13px;
    color: var(--xl-color-outline);
  }

  &__empty {
    margin: 0;
    font-size: var(--xl-type-body-medium-size);
    color: var(--xl-color-secondary-text);
  }
}
</style>
